import React, { useMemo } from 'react'
import { CalendarEventBlock, sortEventsLongestFirst } from './CalendarEventBlock'
import {
  formatTime,
  getCurrentTimeIndicatorTop,
  getDayNameShort,
  getEnabledWeekDays,
  getEventsForDate,
  getWeekStart,
  hourSlots,
  isSameDay,
} from './dateUtils'
import { AvailabilityCalendarEvent, AvailabilitySchedule } from './types'
import {
  DayColumn,
  DayName,
  DayNumberButton,
  TimeGridWrapper,
  TimeIndicator,
  TimeLabel,
  ViewPanel,
  WeekHeader,
  WeekHeaderCell,
  WeekTimeGrid,
} from './style'

interface WeekViewProps {
  date: Date
  events: AvailabilityCalendarEvent[]
  schedule: AvailabilitySchedule
  onSelectDay: (day: Date) => void
}

export const WeekView = ({
  date,
  events,
  schedule,
  onSelectDay,
}: WeekViewProps): JSX.Element => {
  const weekStart = useMemo(() => getWeekStart(date), [date])
  const days = useMemo(
    () => getEnabledWeekDays(weekStart, schedule.workingDays),
    [weekStart, schedule.workingDays]
  )
  const hours = useMemo(
    () => hourSlots(schedule.hourStart, schedule.hourEnd),
    [schedule.hourStart, schedule.hourEnd]
  )
  const today = useMemo(() => new Date(), [])
  const indicatorTop = getCurrentTimeIndicatorTop(
    today,
    schedule.hourStart,
    schedule.hourEnd
  )
  const showIndicator = days.some(day => isSameDay(day, today)) && indicatorTop != null
  const dayCount = Math.max(days.length, 1)

  return (
    <ViewPanel>
      <WeekHeader $dayCount={dayCount}>
        <WeekHeaderCell />
        {days.map(day => (
          <WeekHeaderCell key={day.toISOString()}>
            <DayName>{getDayNameShort(day)}</DayName>
            <DayNumberButton
              type="button"
              $today={isSameDay(day, today)}
              onClick={() => onSelectDay(day)}
            >
              {day.getDate()}
            </DayNumberButton>
          </WeekHeaderCell>
        ))}
      </WeekHeader>

      <TimeGridWrapper>
        <WeekTimeGrid $dayCount={dayCount}>
          {hours.map(hour => (
            <React.Fragment key={hour}>
              <TimeLabel $isFirst={hour === schedule.hourStart}>
                <span>{formatTime(hour)}</span>
              </TimeLabel>
              {days.map(day => {
                const dayEvents = sortEventsLongestFirst(
                  getEventsForDate(events, day).filter(
                    event => event.start.getHours() === hour
                  )
                )
                return (
                  <DayColumn key={`${day.toISOString()}-${hour}`}>
                    {dayEvents.map(event => (
                      <CalendarEventBlock key={event.id} event={event} />
                    ))}
                  </DayColumn>
                )
              })}
            </React.Fragment>
          ))}
          {showIndicator && indicatorTop != null && (
            <TimeIndicator $top={indicatorTop} />
          )}
        </WeekTimeGrid>
      </TimeGridWrapper>
    </ViewPanel>
  )
}
